/* LAV TDMS — Document card (drawer) with 3 layout variants:
   «Сводка» (summary) · «Хронология» (revision/version timeline) · «Документ» (preview split).
   Ported 1:1 from the prototype's app/DocumentCard.jsx (window.APP). Inline styles kept verbatim. */
import { type CSSProperties, type ReactNode, useRef, useState } from 'react'
import { Icon } from '@/ds/icons'
import { ApiError, request, saveFile } from '@/data/api'
import { qk, useItem, useList, useSub, inboxKeys } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import { useDictRows, useDicts, useMe } from '@/data/meta'
import type { Crs, Doc, DocSubmission, DocVersion, LifeStatus, Purpose as PurposeCode, Revision, ReviewCode, Vdoc } from '@/data/types'
import {
  Button,
  Badge,
  Avatar,
  DateCell,
  Purpose,
  CodeChip,
  LifeBadge,
  lifeLabel,
  DiscTag,
  Crit,
  Segmented,
  Select,
  Field,
  Drawer,
  Input,
  DictPicker,
  DateInput,
  Dialog,
  EmptyState,
  Spinner,
  toast,
  clickable,
} from '@/ui'
import { revVer, today } from '@/lib/format'
import { t, type Lang } from '@/i18n/lang'


/** Реестр отдаёт «—», когда ревизии у документа ещё нет. */
const revOf = (doc: Doc | Vdoc): string | null => (doc.rev && doc.rev !== '—' ? doc.rev : null)

/* Скачивание в два шага. Сервер отдаёт подписанный адрес телом ответа, и уже
   по нему идёт браузер: переход по адресу API не несёт токена и упёрся бы в
   401, а подпись хранилища покрывает адрес целиком — любой лишний заголовок,
   в том числе Authorization, её ломает. */
async function downloadFile(no: string, rev: string, ver: number): Promise<void> {
  const path = `/docs/${encodeURIComponent(no)}/revisions/${encodeURIComponent(rev)}/files/${ver}/content`
  try {
    const link = await request<{ url: string | null; notice?: string | null; controlled?: string | null }>('GET', path)
    /* Пометку о недействующей ревизии ставит сервер, и обойти её нечем:
       ссылки на сырой объект в ответе просто нет. PDF приходит собранной
       копией (`controlled`), файл, на который пометку нанести нельзя, —
       ссылкой, в имени которой она названа. */
    if (link.notice) toast({ tone: 'info', title: 'Копия справочная', text: link.notice })
    if (link.controlled) {
      /* Не переход браузера, а запрос с токеном: адрес принадлежит API, и
         навигация по нему пришла бы без заголовка и получила 401. */
      await saveFile(link.controlled.replace(/^\/api/, ''))
      return
    }
    if (!link.url) throw new Error('сервер не отдал адрес файла')
    window.location.assign(link.url)
  } catch (e) {
    toast({
      tone: 'danger',
      title: 'Не удалось получить файл',
      text: e instanceof Error ? e.message : String(e),
    })
  }
}
function cmpDate(a?: string | null, b?: string | null): number {
  const p = (d?: string | null): number => {
    if (!d || d === '—') return 0
    const [dd, mm, yy] = d.split('.')
    return new Date(2000 + +(yy ?? 0), +(mm ?? 1) - 1, +(dd ?? 1)).getTime()
  }
  return p(a) - p(b)
}

const note = (danger?: boolean): CSSProperties => ({ padding: '10px 0', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: danger ? 'var(--danger-ink)' : 'var(--text-subtle)' })

// ---- Date control block -----------------------------------------------
/** Строка сроков стадии — ответ GET /docs/{no}/schedule. */
export interface StageSchedule {
  stage: 'IFR' | 'IFA' | 'AFD'
  plan: string | null
  forecast: string | null
  fact: string | null
}

/* Круг документа — три стадии: выдача на рассмотрение, согласование, принятие
   к производству работ. Срок ведут по каждой, поэтому строк всегда три, даже
   если стадия ещё не наступила: пустая строка это «срок не назначен», а
   отсутствие строки читалось бы как «стадии не будет». */
function DateControl({ doc, lang }: { doc: Doc | Vdoc; lang: Lang }) {
  const query = useSub<StageSchedule[]>('/docs/' + encodeURIComponent(doc.no) + '/schedule')
  const rows = query.data ?? []
  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1fr', padding: '8px 12px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)' }}>
        {[t(lang, 'Назн.', 'Purp.'), t(lang, 'План', 'Plan'), t(lang, 'Прогноз', 'Forecast'), t(lang, 'Факт', 'Actual')]
          .map(h => <span key={h} style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{h}</span>)}
      </div>
      {query.isPending ? (
        /* Спиннер к тексту: голая надпись неотличима от зависания. */
        <div style={{ padding: 12, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={13} />
          {t(lang, 'Загрузка…', 'Loading…')}
        </div>
      ) : rows.map(r => {
        /* Просрочка считается по стадии, а не по документу: прогноз позже
           плана и факта ещё нет — это просрочка именно этой стадии. */
        const overdue = !r.fact && cmpDate(r.forecast, r.plan) > 0
        return (
          <div key={r.stage} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1fr', alignItems: 'center', padding: '9px 12px', borderBottom: '1px solid var(--border-subtle)', opacity: r.stage === doc.purpose || r.plan ? 1 : 0.6 }}>
            <Purpose value={r.stage as PurposeCode} size="sm" />
            <DateCell value={r.plan} kind="planned" />
            <DateCell value={r.forecast} kind={overdue ? 'overdue' : 'forecast'} />
            <DateCell value={r.fact} kind="actual" strong />
          </div>
        )
      })}
    </div>
  )
}

/** Строка истории передач — ответ GET /docs/{no}/transmittals. */
/**
 * Код-ссылка: номер пакета или замечания, по которому можно перейти.
 *
 * <p>Карточка документа ПЕРЕЧИСЛЯЛА коды CRS и TRM, но не вела по ним: человек
 * читал «DP5-CMK-ARM-TRM-0010», запоминал и шёл искать его руками через меню и
 * список — при том что переходы в приложении давно есть. Настоящий `<a>`, а не
 * span: подчёркивание при наведении даёт base.css только тегу ссылки. href нет —
 * переход живёт в обработчике, как и у прочих ссылок карточки.
 *
 * <p>`stopPropagation` обязателен: код стоит и внутри нажимаемых строк
 * (заголовок ревизии в хронологии), и клик по коду не должен сворачивать их.
 */
export function CodeLink({ code, onOpen, title }: { code: string; onOpen?: (code: string) => void; title?: string }) {
  if (!onOpen) return <>{code}</>
  return (
    <a {...clickable((e) => { e.stopPropagation(); onOpen(code) })}
       title={title}
       style={{ color: 'var(--text-link)', cursor: 'pointer', textDecoration: 'none' }}>{code}</a>
  )
}

interface TransferRow {
  id: string
  dir: 'in' | 'out'
  subject: string
  from: string
  to: string
  status: string
  code: string | null
  rev: string | null
  ver: number | null
  date: string | null
  acknowledged: string | null
}

/**
 * История передач документа: чем приходил и чем уходил, в какой ревизии.
 *
 * <p>Ревизия и версия берутся из позиции пакета — они зафиксированы моментом
 * выпуска, а не подобраны сегодняшним состоянием папки.
 */
/* История правок карточки: «было → стало» по полям этого документа.

   Изменения писались с самого начала, но читались только журналом аудита — по
   сессии. После массовой правки трёхсот строк история одного документа в нём
   не находилась. */
function FieldHistory({ doc, lang }: { doc: Doc | Vdoc; lang: Lang }) {
  const query = useSub<{ rows: { field: string; before: string | null; after: string | null; at: string; author: string }[] }>(
    '/docs/' + encodeURIComponent(doc.no) + '/changes')
  const rows = query.data?.rows ?? []
  if (query.isPending) return <div style={{ ...note(), display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
  if (!rows.length) return <div style={note()}>{t(lang, 'Карточку не правили', 'The card has not been edited')}</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-subtle)', flex: '0 0 auto' }}>{r.at}</span>
          <span style={{ color: 'var(--text-muted)', flex: '0 0 auto' }}>{r.author}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)', flex: '0 0 auto' }}>{r.field}</span>
          <span style={{ flex: 1, minWidth: 0, color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {(r.before ?? '—') + ' → ' + (r.after ?? '—')}
          </span>
        </div>
      ))}
    </div>)
}

function TransferHistory({ doc, lang, onOpenTrm }: { doc: Doc | Vdoc; lang: Lang; onOpenTrm?: (id: string) => void }) {
  const query = useSub<{ rows: TransferRow[] }>('/docs/' + encodeURIComponent(doc.no) + '/transmittals')
  const rows = query.data?.rows ?? []
  if (query.isPending) return <div style={{ ...note(), display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
  if (!rows.length) return <div style={note()}>{t(lang, 'Документ ещё не передавали', 'The document has not been transferred yet')}</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r) => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
          <Badge tone={r.dir === 'in' ? 'accent' : 'neutral'} size="sm">{r.dir === 'in' ? t(lang, 'входящий', 'incoming') : t(lang, 'исходящий', 'outgoing')}</Badge>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            <CodeLink code={r.id} onOpen={onOpenTrm} />
          </span>
          <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.subject}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
            {t(lang, 'ред.', 'rev')} {r.rev ?? '—'}{r.ver != null ? ` · v${r.ver}` : ''}
          </span>
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{r.date ?? ''}</span>
          {r.acknowledged ? <Badge tone="success" size="sm">{t(lang, 'получен', 'received')} {r.acknowledged}</Badge> : null}
          <CodeChip code={(r.code ?? null) as ReviewCode | null} size="sm" />
        </div>
      ))}
    </div>
  )
}

/**
 * Связи документа: замена, ссылка, приложение.
 *
 * <p>Команды и чтение были на сервере с самого начала и не вызывались ни
 * одним экраном — связь видна была только косвенно, колонкой описи комплекта.
 */
/**
 * Проектные атрибуты документа: колонки реестра заказчика, которых нет в схеме.
 *
 * <p>Заказчик размечает MDR тегом оборудования, системой, грифом и форматом.
 * Эти колонки исчезали при загрузке, и ТДО вёл их в Excel рядом с реестром.
 * Значения правятся здесь же: список у проекта свой, а карточка одна.
 */
function DocAttrsPanel({ doc, lang }: { doc: Doc | Vdoc; lang: Lang }) {
  const rows = useSub<{ rows: { code: string; title: string; kind: string; dict: string | null; value: string | null }[] }>(
    `/docs/${encodeURIComponent(doc.no)}/attributes`)
  const dictRows = useDicts().data
  const [draft, setDraft] = useState<Record<string, string>>({})
  const save = useCommand<Record<string, string>>({
    method: 'PUT',
    path: () => `/docs/${encodeURIComponent(doc.no)}/attributes`,
    body: (a) => a,
    invalidate: () => [qk.sub(`/docs/${encodeURIComponent(doc.no)}/attributes`), qk.all('audit')],
  })
  const items = rows.data?.rows ?? []
  if (rows.isPending) return <div style={{ padding: 12, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
  if (!items.length) {
    return (
      <div style={{ padding: 12, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
        {t(lang, 'Атрибуты проекта не заведены. Их набор задаёт администратор — по колонкам реестра заказчика.',
                 'No project attributes yet. The administrator defines them from the customer register columns.')}
      </div>
    )
  }
  const dirty = Object.keys(draft).length > 0
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {items.map((a) => {
        const value = draft[a.code] ?? a.value ?? ''
        const options = a.kind === 'list'
          ? ((dictRows as Record<string, { rows: { code: string; name: string }[] }> | undefined)?.[a.dict ?? '']?.rows ?? [])
          : []
        return (
          <div key={a.code} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 10, alignItems: 'center' }}>
            <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{a.title}</span>
            {a.kind === 'list' ? (
              <Select size="sm" value={value} onChange={(e) => setDraft({ ...draft, [a.code]: e.target.value })}>
                <option value="">{t(lang, '— не задано —', '— none —')}</option>
                {options.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}
              </Select>
            ) : (
              <Input size="sm" value={value} onChange={(e) => setDraft({ ...draft, [a.code]: e.target.value })}
                     placeholder={a.kind === 'date' ? 'ДД.ММ.ГГГГ' : ''} />
            )}
          </div>
        )
      })}
      {dirty ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Button variant="secondary" size="sm" onClick={() => setDraft({})}>{t(lang, 'Отменить правки', 'Discard')}</Button>
          <Button variant="primary" size="sm" disabled={save.isPending}
                  onClick={() => save.mutate(draft, {
                    onSuccess: () => { setDraft({}); toast({ tone: 'success', title: t(lang, 'Атрибуты сохранены', 'Attributes saved') }) },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не сохранились', 'Not saved'), text: e.message }),
                  })}>{save.isPending ? t(lang, 'Сохраняем…', 'Saving…') : t(lang, 'Сохранить', 'Save')}</Button>
        </div>
      ) : null}
    </div>
  )
}

function DocLinks({ doc, lang }: { doc: Doc | Vdoc; lang: Lang }) {
  const base = '/docs/' + encodeURIComponent(doc.no) + '/links'
  /* Сервер отдаёт связь целиком: номер второго документа, его название,
     ревизию, состояние и НАПРАВЛЕНИЕ. Экран читал поле «to», которого в ответе
     нет, и печатал пустую строку без направления — «заменяет» и «заменён»
     выглядели одинаково. */
  const query = useSub<{ links: { kind: string; direction: 'in' | 'out'; doc: string; title: string | null
                                 life: string | null; rev: string | null; note: string | null }[] }>(base)
  const links = query.data?.links ?? []
  const [kind, setKind] = useState('references')
  const [to, setTo] = useState('')
  const add = useCommand<{ kind: string; to: string }>({
    path: () => base,
    body: (a) => ({ kind: a.kind, to: a.to }),
    invalidate: () => [qk.sub(base), qk.all('audit')],
  })
  /* Ошибочную связь снимают: она сведение о документе, а не событие. */
  const drop = useCommand<{ to: string }>({
    method: 'DELETE',
    path: (a) => `${base}/${encodeURIComponent(a.to)}`,
    invalidate: () => [qk.sub(base), qk.all('audit')],
  })
  const KINDS: Record<string, { ru: string; en: string }> = {
    supersedes: { ru: 'заменяет', en: 'supersedes' },
    references: { ru: 'ссылается на', en: 'references' },
    attachment: { ru: 'приложение к', en: 'attachment to' },
  }
  /* Обратная сторона той же связи: хранится одна строка, а новости разные. */
  const BACK: Record<string, { ru: string; en: string }> = {
    supersedes: { ru: 'заменён документом', en: 'superseded by' },
    references: { ru: 'на него ссылается', en: 'referenced by' },
    attachment: { ru: 'имеет приложение', en: 'has attachment' },
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {query.isPending ? <div style={{ ...note(), display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div> : null}
      {links.map((l) => {
        const words = l.direction === 'in' ? BACK[l.kind] : KINDS[l.kind]
        return (
          <div key={l.kind + l.direction + l.doc} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
            <Badge tone="neutral" size="sm">{t(lang, words?.ru ?? l.kind, words?.en ?? l.kind)}</Badge>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-link)' }}>{l.doc}</span>
            <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.title ?? ''}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-subtle)' }}>{t(lang, 'рев.', 'rev.')} {l.rev ?? '—'}</span>
            {l.life ? <LifeBadge life={l.life as LifeStatus} /> : null}
            {l.note ? <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{l.note}</span> : null}
            <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={12} />} disabled={drop.isPending}
                    title={t(lang, 'Снять связь', 'Remove the link')}
                    onClick={() => drop.mutate({ to: l.doc }, {
                      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не снято', 'Not removed'), text: e.message }),
                    })}>{t(lang, 'Снять', 'Remove')}</Button>
          </div>
        )
      })}
      {!query.isPending && !links.length ? <div style={note()}>{t(lang, 'Связей нет', 'No links')}</div> : null}
      <div style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
        <Select label={t(lang, 'Связь', 'Link')} value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 180 }}>
          {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{t(lang, v.ru, v.en)}</option>)}
        </Select>
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={t(lang, '№ документа', 'document no')} style={{ flex: 1 }} />
        <Button variant="secondary" size="sm" disabled={!to.trim() || add.isPending}
                onClick={() => add.mutate({ kind, to: to.trim() }, {
                  onSuccess: () => { setTo(''); toast({ tone: 'success', title: t(lang, 'Связь добавлена', 'Link added') }) },
                })}>{t(lang, 'Связать', 'Link')}</Button>
      </div>
    </div>
  )
}

// ---- Полоса жизненного цикла --------------------------------------------

/**
 * Шаги круга по порядку. Итог — один шаг на три исхода: он и так подписан
 * состоянием, а три клетки подряд, из которых занята одна, читаются как
 * «пройдено две трети», чего не было.
 */
const LIFE_TRACK: LifeStatus[] = ['planned', 'received', 'review', 'crs']

/** Исходы круга: у каждого свой шаг «что дальше». */
const LIFE_OUTCOME: LifeStatus[] = ['reissue', 'approved', 'accepted', 'afd']

/** Состояния вне круга: работа по документу не идёт. */
const LIFE_OFF: LifeStatus[] = ['hold', 'superseded', 'void']

/**
 * Где документ на полосе и что с ним делать дальше.
 *
 * <p>Чистая функция, потому что «что дальше» — это правило домена, а не
 * оформление: из «принят с замечаниями» выходят следующей ВЕРСИЕЙ, из
 * «принят» — следующей РЕВИЗИЕЙ, и перепутать их значит объяснить человеку
 * ровно наоборот.
 *
 * @returns `step` — индекс пройденного шага в LIFE_TRACK, −1 для исхода и
 *          состояний вне круга; `next` — подсказка или `null`, когда ждать
 *          нечего.
 */
export function lifeTrackAt(life: LifeStatus): { step: number; outcome: LifeStatus | null; next: { ru: string; en: string } | null } {
  const outcome = LIFE_OUTCOME.includes(life) ? life : null
  const step = LIFE_TRACK.indexOf(life)
  if (LIFE_OFF.includes(life)) {
    return { step: -1, outcome: null, next: null }
  }
  const next =
    life === 'planned' ? { ru: 'Приложите файл и подайте трансмитталом', en: 'Attach a file and submit it with a transmittal' }
    : life === 'received' ? { ru: 'Ждём назначения круга: входной контроль', en: 'Waiting for the round to open: incoming check' }
    : life === 'review' ? { ru: 'Идёт рассмотрение', en: 'Under review' }
    : life === 'crs' ? { ru: 'Замечания выпущены — ждём решения утверждающего', en: 'Comments issued — waiting for the approver' }
    : life === 'reissue' ? { ru: 'Не принято: учтите замечания и подайте следующую ВЕРСИЮ', en: 'Not accepted: address the comments and submit the next VERSION' }
    : life === 'approved' ? { ru: 'Принято с замечаниями: учтите их и подайте следующую ВЕРСИЮ — ревизия не меняется', en: 'Accepted with comments: address them and submit the next VERSION — the revision stays' }
    : life === 'accepted' ? { ru: 'Принято: подайте чистовую ВЕРСИЮ в назначении AFD — «Согласован» даёт только её приёмка', en: 'Accepted: submit the clean VERSION as AFD — only its acceptance grants «Approved»' }
    : life === 'afd' ? { ru: 'Согласован. Следующий выпуск — новая РЕВИЗИЯ', en: 'Approved. The next issue is a new REVISION' }
    : null
  return { step, outcome, next }
}

/**
 * Полоса круга в шапке карточки.
 *
 * <p>Состояние показывал один бейдж, и по нему не читалось ни где документ на
 * круге, ни что с ним делать. Хуже всего это било по коду B: «Принят с
 * замечаниями» выглядит как конец, а на деле требует следующей версии.
 */
function LifeTrack({ life, rev, ver, lang }: { life: LifeStatus; rev: string; ver: number; lang: Lang }) {
  const { step, outcome, next } = lifeTrackAt(life)
  if (LIFE_OFF.includes(life)) {
    return (
      <div style={{ marginTop: 8, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
        {t(lang, 'Работа по документу не идёт: ', 'Work on the document has stopped: ')}{lifeLabel(life)}
      </div>
    )
  }
  const cell = (done: boolean, here: boolean, label: string, key: string) => (
    <span key={key} style={{
      padding: '2px 8px',
      borderRadius: 'var(--radius-sm)',
      whiteSpace: 'nowrap',
      font: 'var(--font-caption)',
      fontWeight: here ? 600 : 400,
      color: here ? 'var(--accent-ink)' : done ? 'var(--text-muted)' : 'var(--text-subtle)',
      background: here ? 'var(--accent-soft)' : 'transparent',
      boxShadow: here ? 'inset 0 0 0 1px var(--accent-line)' : 'none',
      opacity: done || here ? 1 : 0.55,
    }}>{label}</span>
  )
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {LIFE_TRACK.map((s, i) =>
          cell(step < 0 || i < step, i === step, lifeLabel(s), s))}
        <span style={{ color: 'var(--text-subtle)', font: 'var(--font-caption)' }}>→</span>
        {cell(false, outcome != null, outcome ? lifeLabel(outcome) : t(lang, 'Итог круга', 'Verdict'), 'outcome')}
      </div>
      {next ? (
        <div style={{ marginTop: 4, font: 'var(--font-caption)', color: 'var(--text-muted)' }}>
          {/* Ревизия и версия названы рядом с подсказкой: разница между ними и
              есть то, что человек путает, получив код B. */}
          {t(lang, `Ревизия ${rev} · версия ${ver} — `, `Revision ${rev} · version ${ver} — `)}
          {t(lang, next.ru, next.en)}
        </div>
      ) : null}
    </div>
  )
}

// ---- Revision timeline -------------------------------------------------
/**
 * Ключ строки хронологии.
 *
 * <p>Одна и та же ревизия приходит дважды: подрядчик подаёт «рев. 00»,
 * получает возврат и подаёт её снова другим письмом. Ключом строки был номер
 * ревизии — React ругался на повторяющиеся ключи (и вправе схлопнуть строки),
 * а раскрытие одной строки раскрывало обе, потому что они «одна». Ключ
 * собирается из самой подачи: письмо и дата у них разные. Порядковый номер
 * замыкает случай, когда и они совпали, — ключ обязан быть уникальным.
 */
export function revisionKey(r: { rev: string; trm: string; date: string }, i: number): string {
  return `${r.rev}\u00b7${r.trm}\u00b7${r.date}\u00b7${i}`
}

function RevisionTimeline({ doc, lang, expanded, onOpenTrm }: { doc: Doc | Vdoc; lang: Lang; expanded?: boolean
                                                              onOpenTrm?: (id: string) => void }) {
  const base = '/docs/' + encodeURIComponent(doc.no)
  const revsQuery = useSub<Revision[]>(base + '/revisions')
  /* undefined — «пользователь ещё не выбирал». Раскрытая ревизия вычисляется, а
     не кладётся в состояние при монтировании: на первом рендере ответа сервера
     ещё нет, и первой ревизии, которую надо раскрыть, тоже. */
  const [picked, setPicked] = useState<string | null | undefined>(undefined)
  const revs = revsQuery.data ?? []
  const open = picked === undefined
    ? (expanded && revs[0] ? revisionKey(revs[0], 0) : null)
    : picked
  /* Файлы хранятся за НОМЕРОМ ревизии, а не за подачей: раскрытая строка
     спрашивает свой номер. */
  const openRev = revs.find((r, i) => revisionKey(r, i) === open)?.rev ?? null
  /* Версии запрашиваются только для раскрытой ревизии: в остальные никто не
     смотрит, а запрос на каждую — это столько же обращений, сколько выпусков. */
  const versQuery = useSub<DocVersion[]>(openRev ? base + '/versions/' + encodeURIComponent(openRev) : null)
  /* Снятие ошибочно загруженной версии. Причина спрашивается: снятие без неё
     не объяснимо ни автору, ни аудиту, и сервер откажет. */
  const removeFile = useCommand<{ rev: string; ver: number; reason: string }>({
    method: 'DELETE',
    path: (a) => `${base}/revisions/${encodeURIComponent(a.rev)}/files/${a.ver}?reason=${encodeURIComponent(a.reason)}`,
    invalidate: (a) => [qk.sub(base + '/versions/' + encodeURIComponent(a.rev)), qk.sub(base + '/revisions'),
                        qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })
  /* Удаление байтов из хранилища — право администрирования, а не «изменять
     реестр»: снятие оставляет объект на месте, а это единственное место в
     системе, где файла не остаётся вовсе. Кнопка показывается по тому же
     праву, каким сервер и отбивает команду. */
  const isAdmin = useMe().data?.perms?.admin === 'rw'
  const [purging, setPurging] = useState<{ rev: string; ver: number; file: string } | null>(null)

  if (revsQuery.isPending) return <div style={{ ...note(), display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
  if (revsQuery.isError) return <div style={note(true)}>{t(lang, 'Не удалось загрузить историю ревизий', 'Failed to load the revision history')}</div>
  if (!revs.length) return <EmptyState icon="file-plus" title={t(lang, 'Ревизий нет', 'No revisions')} text={t(lang, 'Ревизия создаётся при поступлении трансмиттала с документом.', 'A revision is created when a transmittal carrying the document arrives.')} />
  return (
    <div style={{ position: 'relative', paddingLeft: 4 }}>
      {revs.map((r, i) => {
        const key = revisionKey(r, i)
        const isOpen = open === key
        const vers = isOpen ? versQuery.data ?? null : null
        const last = i === revs.length - 1
        /* Повторная подача того же номера — типовой случай после возврата:
           без пометки две строки «Ревизия 00» читаются как задвоение. */
        const again = revs.some((o, j) => j < i && o.rev === r.rev)
        return (
          <div key={key} style={{ position: 'relative', paddingLeft: 26, paddingBottom: last ? 0 : 18 }}>
            {!last ? <span style={{ position: 'absolute', left: 8, top: 22, bottom: 0, width: 2, background: 'var(--border-default)' }} /> : null}
            <span style={{ position: 'absolute', left: 0, top: 3, width: 18, height: 18, display: 'grid', placeItems: 'center', borderRadius: 999, background: i === 0 ? 'var(--accent)' : 'var(--surface-card)', border: '2px solid ' + (i === 0 ? 'var(--accent)' : 'var(--border-strong)'), fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, color: i === 0 ? 'var(--on-accent)' : 'var(--text-muted)' }}>{r.rev}</span>
            <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--surface-card)' }}>
              <button onClick={() => setPicked(isOpen ? null : key)} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>Ревизия {r.rev}</span>
                {again ? <span style={{ font: 'var(--font-caption)', color: 'var(--accent-ink)' }}>{t(lang, 'повторная подача', 'resubmission')}</span> : null}
                <Purpose value={r.purpose} size="sm" /><CodeChip code={r.code} size="sm" />
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    <CodeLink code={r.trm} onOpen={onOpenTrm} />
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{r.date}</span>
                  <span style={{ color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={15} /></span>
                </span>
              </button>
              <div style={{ padding: '0 12px 10px 12px' }}>
                <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}><Avatar name={r.by} size={18} /> {r.by} · {r.note}</div>
              </div>
              {isOpen && vers ? (
                /* Полка приходит ПОЗЖЕ клика — по ответу versQuery: проявление
                   вместо скачка, чтобы появление «само по себе» не дёргало блок. */
                <div style={{ borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)', animation: 'fade var(--duration-fast) var(--ease-out)' }}>
                  <div style={{ padding: '7px 12px', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{t(lang, 'Версии файлов внутри ревизии', 'File versions')}</div>
                  {vers.map(v => (
                    <div key={v.v} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 2, padding: '1px 5px' }}>v{v.v}</span>
                      <span style={{ color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name="file-text" size={14} /></span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-body)' }}>{v.file}</span>
                      <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{v.size}</span>
                      <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{v.date}</span>
                      <button onClick={() => void downloadFile(doc.no, r.rev, v.v)} aria-label={t(lang, 'Скачать', 'Download')} className="icon-btn" style={{ display: 'grid', placeItems: 'center', width: 24, height: 24, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer' }}><Icon name="download" size={14} /></button>
                      {/* Снятие ошибочной версии: строка становится могилой,
                          объект в хранилище остаётся, а переданную пакетом
                          версию сервер снять не даст. */}
                      <button onClick={() => {
                        const reason = window.prompt(t(lang, 'Причина снятия файла', 'Reason for removing the file'))
                        if (!reason || !reason.trim()) return
                        removeFile.mutate({ rev: r.rev, ver: v.v, reason: reason.trim() }, {
                          onSuccess: () => toast({ tone: 'info', title: t(lang, 'Файл снят', 'File removed'), text: `${r.rev} · v${v.v}` }),
                        })
                      }} aria-label={t(lang, 'Снять файл', 'Remove file')} className="icon-btn"
                              style={{ display: 'grid', placeItems: 'center', width: 24, height: 24, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer' }}><Icon name="trash-2" size={14} /></button>
                      {/* Рядом со снятием, но не то же самое: снятие убирает
                          строку, это — сам объект. Своя иконка и красный цвет,
                          потому что перепутать их можно ровно один раз. */}
                      {isAdmin ? (
                        <button onClick={() => setPurging({ rev: r.rev, ver: v.v, file: v.file })}
                                aria-label={t(lang, 'Удалить файл из хранилища', 'Delete the file from storage')}
                                title={t(lang, 'Удалить файл из хранилища — байты стираются насовсем', 'Delete the file from storage — the bytes are gone for good')}
                                className="icon-btn"
                                style={{ display: 'grid', placeItems: 'center', width: 24, height: 24, border: 0, background: 'transparent', color: 'var(--danger-ink)', cursor: 'pointer' }}><Icon name="eraser" size={14} /></button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : isOpen ? (
                /* Версии ещё едут: заглушка держит высоту, иначе блок прыгает
                   второй раз — уже по приходу данных. */
                <div style={{ borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)', padding: '12px' }}>
                  <span className="skel-cell" style={{ width: '60%' }} />
                </div>
              ) : null}
            </div>
          </div>
        )
      })}
      {purging ? <PurgeFileDialog base={base} lang={lang} target={purging} onClose={() => setPurging(null)} /> : null}
    </div>
  )
}

/**
 * Удаление файла из хранилища.
 *
 * <p>Единственное место в системе, где что-либо стирается физически: снятие
 * файла и аннулирование документа оставляют байты на месте. Поэтому не
 * window.confirm, а окно, называющее последствие словами: восстановить нечем.
 *
 * <p>Причина обязательна, потому что после удаления от файла остаётся ровно
 * одна вещь — запись журнала, и запись без причины не объясняет ничего ни через
 * год, ни на разборе.
 *
 * <p>Уехавший трансмитталом файл требует второго шага. Он у второй стороны на
 * руках, и стирание у себя означает расхождение с тем, что она получила: молча
 * так делать нельзя. Запрещать вовсе — тоже: убрать требуют как раз то, что уже
 * уехало. Поэтому подтверждение спрашивается ПОСЛЕ отказа сервера, а не
 * отправляется вперёд него: сам факт передачи знает сервер, и вкладка, решившая
 * это за него, подтверждала бы то, чего нет.
 */
function PurgeFileDialog({ base, lang, target, onClose }: {
  base: string
  lang: Lang
  target: { rev: string; ver: number; file: string }
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  /* Отказ живёт в своём состоянии, а не читается из мутации: на время второй
     попытки её error обнуляется, и предупреждение о передаче мигало бы вместе
     с подписью кнопки — ровно в тот момент, когда его читают. */
  const [alreadySent, setAlreadySent] = useState(false)
  const [failed, setFailed] = useState<ApiError | Error | null>(null)
  const purge = useCommand<{ rev: string; ver: number; reason: string; force: boolean }>({
    method: 'DELETE',
    path: (a) => `${base}/revisions/${encodeURIComponent(a.rev)}/files/${a.ver}/object`,
    body: (a) => ({ reason: a.reason, force: a.force }),
    invalidate: (a) => [qk.sub(base + '/versions/' + encodeURIComponent(a.rev)), qk.sub(base + '/revisions'),
                        qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })
  const submit = () => {
    // Прошлый отказ рядом с занятой кнопкой читается как свежий.
    setFailed(null)
    purge.mutate({ rev: target.rev, ver: target.ver, reason: reason.trim(), force: alreadySent }, {
      onSuccess: () => {
        onClose()
        toast({ tone: 'info', title: t(lang, 'Файл удалён из хранилища', 'File deleted from storage'), text: target.file })
      },
      onError: (e) => {
        if (e instanceof ApiError && e.code === 'file.already-sent') setAlreadySent(true)
        setFailed(e)
      },
    })
  }
  /* Отказ показывается словами сервера: почему именно нельзя, знает он. */
  const api = failed instanceof ApiError ? failed : null
  return (
    <Dialog open onClose={onClose} title={t(lang, 'Удалить файл из хранилища', 'Delete the file from storage')}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          {t(lang, `Ревизия ${target.rev}, версия ${target.ver}: `, `Revision ${target.rev}, version ${target.ver}: `)}
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-strong)' }}>{target.file}</span>
        </div>
        <div style={{ padding: '10px 12px', border: '1px solid var(--danger-line)', background: 'var(--danger-bg)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          {t(lang, 'Байты стираются из хранилища насовсем — не «строка снята», а файла больше нет. Восстановить его неоткуда, отдать второй стороне повторно нечем, и в системе от него останется одна запись журнала.',
                   'The bytes are erased from storage for good — not «the row is withdrawn», the file is gone. There is nowhere to restore it from, nothing to hand to the other party again, and one audit record is all that stays.')}
        </div>
        <Input label={t(lang, 'Причина удаления', 'Reason for the deletion')} value={reason}
               onChange={(e) => setReason(e.target.value)}
               placeholder={t(lang, 'чужой договор, персональные данные, документ смежника…', 'someone else’s contract, personal data, a neighbour’s document…')}
               hint={t(lang, 'Обязательна: после удаления объяснить файл будет нечем, кроме этой строки в журнале.',
                             'Required: after the deletion this line in the log is the only thing left to explain the file.')} />
        {api ? (
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
            <div style={{ fontWeight: 600, color: 'var(--danger-ink)' }}>{api.title}</div>
            {api.detail ? <div style={{ marginTop: 2, color: 'var(--text-body)' }}>{api.detail}</div> : null}
          </div>
        ) : failed ? (
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--danger-ink)' }}>{failed.message}</div>
        ) : null}
        {alreadySent ? (
          /* Второй шаг появляется спокойным проявлением, а не скачком: блок
             сдвигает подвал ровно в момент, когда читают необратимое
             предупреждение. @starting-style работает только из CSS-класса,
             поэтому локальный <style>. */
          <>
            <style>{`.tdms-purge-sent { transition: opacity var(--duration-normal) var(--ease-out); } @starting-style { .tdms-purge-sent { opacity: 0; } }`}</style>
            <div className="tdms-purge-sent" style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              {t(lang, 'Файл уже ушёл трансмитталом и лежит у второй стороны. Удаление здесь её копию не отзовёт: у вас файла не станет, у неё он останется — и по журналу это будет расхождением с контрагентом, а не ошибкой загрузки.',
                       'The file has already gone out by transmittal and sits with the other party. Deleting it here will not recall their copy: it will be gone on your side and still there on theirs — and the log will record a divergence with the counterparty, not an upload mistake.')}
            </div>
          </>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={purge.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="danger" disabled={reason.trim() === '' || purge.isPending} onClick={submit}
                  title={reason.trim() === '' ? t(lang, 'Причина обязательна', 'The reason is required') : undefined}>
            {purge.isPending
              ? t(lang, 'Удаление…', 'Deleting…')
              : alreadySent
                ? t(lang, 'Всё равно удалить переданный файл', 'Delete the sent file anyway')
                : t(lang, 'Удалить из хранилища', 'Delete from storage')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ---- CRS mini-list -----------------------------------------------------
function CrsMini({ crs, lang, onOpenCrs }: { crs: Crs[]; lang: Lang; onOpenCrs?: (code: string) => void }) {
  if (!crs.length) return <div style={note()}>{t(lang, 'Замечаний по документу нет.', 'No comments on this document.')}</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {crs.map((c) => (
        <div key={c.id} style={{ border: '1px solid var(--border-default)', borderLeft: '3px solid ' + (c.status === 'open' ? 'var(--danger-ink)' : 'var(--green-500)'), borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600 }}>
              <CodeLink code={c.id} onOpen={onOpenCrs} />
            </span>
            <CodeChip code={c.code} size="sm" />
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>рев. {revVer(c.rev, c.ver)} · лист {c.page} · {c.clause}</span>
            <span style={{ marginLeft: 'auto' }}>{c.status === 'open' ? <Badge tone="danger" size="sm" dot>Открыто</Badge> : <Badge tone="success" size="sm" dot>Отвечено</Badge>}</span>
          </div>
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{c.text}</div>
          {c.acsr ? <div style={{ marginTop: 7, padding: '7px 9px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}><b style={{ color: 'var(--text-body)' }}>ACSR · {c.acsr.disp}:</b> {c.acsr.text}</div> : null}
        </div>
      ))}
    </div>
  )
}

/* Файлы документа.
   Список собирался из номера документа: «<№>_<рев>.pdf» на 4,2 МБ,
   «<№>_native.dwg» и сопроводительное письмо. Таких записей нет ни в базе, ни в
   хранилище — выдумка удалена, теперь показаны настоящие версии действующей
   ревизии, те же, что и в «Хронологии». */

/* Шаги загрузки. Названы словами, а не долей процентов: байты уходят прямо в
   хранилище мимо приложения, сколько их прошло — браузер не сообщает, и полоса
   прогресса здесь была бы нарисованной. */
type UploadStep = 'sign' | 'put' | 'register'
const STEP_LABEL: Record<UploadStep, { ru: string; en: string }> = {
  sign: { ru: 'подписываем', en: 'signing' },
  put: { ru: 'загружаем', en: 'uploading' },
  register: { ru: 'регистрируем', en: 'registering' },
}

/* Роль файла в ревизии. Порядок — как в CHECK таблицы (миграция V10). */
const KIND_LABEL = {
  main: { ru: 'Основной', en: 'Main' },
  additional: { ru: 'Дополнительный', en: 'Additional' },
  original: { ru: 'Исходник', en: 'Original' },
} as const

/* Порог для контрольной суммы. crypto.subtle.digest читает файл в память
   целиком и считает синхронно для страницы: на рабочих чертежах в сотни
   мегабайт вкладка замирает на секунды. Сервер принимает регистрацию без
   суммы, поэтому большой файл грузится без неё — это честнее замершего окна. */
const SHA_LIMIT = 100 * 1024 * 1024

/**
 * Файлы ревизии по версиям чертежа.
 *
 * Версия — номер ОСНОВНОГО файла: так её считает и сервер (V16), потому что
 * счётчик версий тратит каждая роль, и загрузка исходника иначе поднимала бы
 * «версию чертежа». Значит исходник и дополнительное вложение принадлежат той
 * версии, при которой их приложили, а не своей собственной — плоский список
 * этого не показывал, и в карточке нельзя было понять, что к чему относится.
 *
 * Версия 0 — файлы, приложенные до первого основного: так бывает, когда
 * ревизию открыли исходником.
 */
export function filesByVersion(files: DocVersion[]): { ver: number; files: DocVersion[] }[] {
  const out: { ver: number; files: DocVersion[] }[] = []
  for (const f of [...files].sort((a, b) => a.v - b.v)) {
    const main = (f.kind ?? 'main') === 'main'
    if (main || !out.length) out.push({ ver: main ? f.v : 0, files: [f] })
    else out[out.length - 1]!.files.push(f)
  }
  return out
}

/**
 * Какой подачей уехал каждый набор файлов.
 *
 * <p>Сопоставить их умеет только тот, кто знает состав набора: позиция письма
 * помнит ОДНУ версию файла на весь набор — старшую из ушедших, — а не по файлу.
 * Значит набор отправлен, если его основной файл — самый поздний из тех, что
 * не превосходят запомненную письмом версию.
 *
 * <p>Наивное сопоставление «версия файла равна запомненной» здесь неверно и
 * молча: типовой набор «чертёж, затем расчёт вложением» запоминает версию
 * ВЛОЖЕНИЯ, и основной файл не нашёлся бы — весь ушедший набор подписался бы
 * «Черновик».
 *
 * @returns номер подачи для каждого набора по порядку; `null` — набор не
 *          отправляли, это черновик
 */
export function submissionOf(
  groups: { ver: number }[],
  submissions: { sub: number; fileVer: number | null }[],
): (number | null)[] {
  const mains = groups.map((g) => g.ver).filter((v) => v > 0).sort((a, b) => a - b)
  const owner = new Map<number, number>()
  for (const s of submissions) {
    if (s.fileVer == null) continue
    // Самый поздний основной файл, не превосходящий ушедшей версии.
    const main = mains.filter((v) => v <= s.fileVer!).at(-1)
    if (main != null && !owner.has(main)) owner.set(main, s.sub)
  }
  return groups.map((g) => (g.ver > 0 ? owner.get(g.ver) ?? null : null))
}

/**
 * Подпись набора файлов.
 *
 * <p>Набор, ушедший трансмитталом, называется ПОДАЧЕЙ — «v00», «v01»: это
 * номер присылки внутри ревизии, и по нему на документ ссылаются обе стороны.
 * Набор, который ещё не отправляли, номера не имеет вовсе — он черновик, и
 * подрядчик волен его править и снимать.
 *
 * <p>Прежде здесь стоял сквозной счётчик ФАЙЛОВ: «Версия 1», потом «Версия 6»
 * — числа, которых заказчик никогда не видел и по которым сослаться нельзя.
 */
export function versionLabel(sub: number | null, lang: Lang): string {
  if (sub == null) return t(lang, 'Черновик', 'Draft')
  return `v${String(sub).padStart(2, '0')}`
}

/** Состояние текстового слоя одной версии файла. */
interface FileTextState {
  state: string
  pages: number
  ocrPages: number
  ocrLimit: number | null
  recognized?: boolean
  error?: string | null
}

/**
 * Строка о поиске по содержимому файла.
 *
 * <p>Показывается не всегда: у обычного чертежа с текстовым слоем говорить
 * нечего, и лишняя строка в карточке только шумит. Говорить надо там, где
 * поиск НЕ работает или работает по распознанному тексту — то есть менее
 * надёжно, чем человек вправе ожидать.
 */
function TextState({ doc, rev, ver, lang }: { doc: string; rev: string; ver: number; lang: Lang }) {
  const path = `/docs/${encodeURIComponent(doc)}/revisions/${encodeURIComponent(rev)}/files/${ver}/text-state`
  const q = useSub<FileTextState>(path)
  const rest = useCommand<void>({
    path: () => `/docs/${encodeURIComponent(doc)}/revisions/${encodeURIComponent(rev)}/files/${ver}/ocr`,
    invalidate: () => [qk.sub(path), qk.all('audit')],
  })
  const st = q.data
  if (!st || st.state === 'done' || st.state === 'skipped') return null

  const line =
    st.state === 'queued' ? t(lang, 'Содержимое ещё не разобрано — поиск по тексту файла пока не работает',
                                   'Contents not parsed yet — text search does not cover this file')
    : st.state === 'ocr-queued' ? t(lang, 'Текстового слоя нет, файл в очереди на распознавание',
                                         'No text layer; queued for recognition')
    : st.state === 'ocr' ? t(lang, `Распознано листов: ${st.ocrPages}${st.ocrLimit ? ` из первых ${st.ocrLimit}` : ''}`,
                                  `Recognized pages: ${st.ocrPages}`)
    : st.state === 'ocr-empty' ? t(lang, 'Распознавание не дало текста: скан нечитаем или лист пуст',
                                        'Recognition found no text: the scan is unreadable or the sheet is blank')
    : st.state === 'empty' ? t(lang, 'Текстового слоя нет — поиск по содержимому недоступен',
                                    'No text layer — content search is unavailable')
    : t(lang, 'Разобрать файл не удалось', 'Failed to parse the file')

  const mayAsk = st.state === 'ocr' && st.ocrLimit != null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
      <Icon name={st.state === 'ocr' ? 'scan-search' : 'info'} size={13} />
      <span style={{ minWidth: 0 }}>{line}</span>
      {mayAsk ? (
        <button onClick={() => rest.mutate()} disabled={rest.isPending}
                style={{ marginLeft: 'auto', border: 0, background: 'transparent', color: 'var(--text-link)', cursor: 'pointer', font: 'var(--font-caption)' }}>
          {rest.isPending ? t(lang, 'Ставим в очередь…', 'Queueing…') : t(lang, 'Распознать остальные листы', 'Recognize the rest')}
        </button>
      ) : null}
    </div>
  )
}

async function sha256Hex(file: File): Promise<string | null> {
  if (file.size > SHA_LIMIT) return null
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

function FilesList({ doc, lang }: { doc: Doc | Vdoc; lang: Lang }) {
  const base = '/docs/' + encodeURIComponent(doc.no)
  const rev = revOf(doc)
  const versQuery = useSub<DocVersion[]>(rev ? base + '/versions/' + encodeURIComponent(rev) : null)
  /* Подачи ревизии: которым кругом уехал каждый набор. Отдельным запросом,
     потому что подача — свойство НАБОРА, а не файла (см. submissionOf). */
  const subsQuery = useSub<DocSubmission[]>(
    rev ? `${base}/revisions/${encodeURIComponent(rev)}/submissions` : null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<UploadStep | null>(null)
  const [failed, setFailed] = useState<UploadStep | null>(null)
  /* Роль файла выбирается до загрузки, а не после: у одной ревизии бывает
     основной чертёж, дополнительное вложение и исходник. */
  const [kind, setKind] = useState('main')
  /* Имя файла проверяется маской, и узнать правило можно было только отказом:
     кнопка открывала выбор файла, а «ожидается …» приходило после.

     Имя спрашивается У СЕРВЕРА, а не собирается здесь. Прежде карточка
     подставляла в шаблон сама — правило одно, реализации две, и расходились
     они молча: подсказка называла одно имя, загрузка ждала другого. Теперь
     отвечает тот же вызов, каким имя и проверяется. Пусто — у маски нет
     состава, проверка выключена, и подсказывать нечего. */
  const expectedQuery = useSub<{ name: string | null }>(
    rev ? `${base}/revisions/${encodeURIComponent(rev)}/expected-name?kind=${encodeURIComponent(kind)}` : null)
  const expectedName = expectedQuery.data?.name ?? null

  /* Обозначение ревизии не передаём: его считает сервер («00», «01», …).
     Считать следующую здесь значит однажды разойтись с базой и получить
     409 revision.duplicate. Выпустить ревизию можно только у принятого в AFD
     документа — до приёмки круг идёт версиями, и сервер это отбивает. */
  const issueRev = useCommand<void, { no: string; rev: string }>({
    path: () => base + '/revisions',
    body: () => ({}),
    invalidate: () => [qk.sub(base + '/revisions'), qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })

  /* Регистрация версии — команда: она пишет строку и аудит. Намерение и сам
     PUT в хранилище командами не являются и идут мимо useCommand. */
  const registerFile = useCommand<{ rev: string; objectKey: string; sha256: string | null; kind: string }>({
    path: (a) => `${base}/revisions/${encodeURIComponent(a.rev)}/files`,
    body: (a) => ({ objectKey: a.objectKey, sha256: a.sha256, kind: a.kind }),
    invalidate: (a) => [
      qk.sub(base + '/revisions'),
      qk.sub(base + '/versions/' + encodeURIComponent(a.rev)),
      qk.all('docs'),
      qk.all('vdocs'),
      qk.all('audit'),
    ],
  })

  const busy = step !== null || issueRev.isPending

  const upload = async (file: File) => {
    if (!rev) return
    setFailed(null)
    /* Тип уходит и в подпись, и в сам PUT: подпись включает Content-Type, и
       расхождение отбивается хранилищем как несовпадение подписи. */
    const contentType = file.type || 'application/octet-stream'
    // Шаг, на котором находимся, — в переменной, а не только в состоянии:
    // в catch попадёт значение из замыкания, то есть предыдущего рендера.
    let at: UploadStep = 'sign'
    try {
      setStep(at)
      const sha256 = await sha256Hex(file)
      /* Роль уходит уже в намерение: маска имени зависит от роли, и без неё
         сервер сверял бы «№_Исходник.dwg» с шаблоном основного PDF. */
      const intent = await request<{ uploadUrl: string; objectKey: string }>(
        'POST',
        `${base}/revisions/${encodeURIComponent(rev)}/upload-intent`,
        { fileName: file.name, contentType, size: file.size, kind },
      )
      at = 'put'; setStep(at)
      /* Ни токена, ни cookie: ссылка уже подписана, а лишний заголовок ломает
         подпись. Поэтому здесь голый fetch, а не клиент запросов. */
      const res = await fetch(intent.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } })
      if (!res.ok) throw new Error(`PUT ${res.status}`)
      at = 'register'; setStep(at)
      await registerFile.mutateAsync({ rev, objectKey: intent.objectKey, sha256, kind })
    } catch (e) {
      setFailed(at)
      /* Отказ команды уже показан общей воронкой мутаций (main.tsx). Намерение
         и загрузка в хранилище через неё не проходят — о них сообщаем сами. */
      if (at !== 'register') {
        toast({
          tone: 'danger',
          title: t(lang, 'Файл не загружен', 'Upload failed'),
          text: `${t(lang, STEP_LABEL[at].ru, STEP_LABEL[at].en)}: ${e instanceof Error ? e.message : String(e)}`,
        })
      }
    } finally {
      setStep(null)
    }
  }

  const files = versQuery.data ?? []
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rev == null ? (
        <div style={note()}>{t(lang, 'У документа ещё нет ревизии — файлу некуда лечь.', 'The document has no revision yet — there is nowhere to put a file.')}</div>
      ) : versQuery.isPending ? (
        <div style={{ ...note(), display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
      ) : versQuery.isError ? (
        <div style={note(true)}>{t(lang, 'Не удалось загрузить список файлов', 'Failed to load the file list')}</div>
      ) : files.length ? (
        filesByVersion(files).map((g, gi, all) => (
          <div key={g.ver} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Заголовок версии: замечания привязаны к ней, и по коду замечания
                (…-A-2-COMM-0001) человек ищет здесь именно версию 2. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
              <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
                {g.ver
                  ? versionLabel(submissionOf(all, subsQuery.data ?? [])[gi] ?? null, lang)
                  : t(lang, 'До основного файла', 'Before the main file')}
              </span>
              <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
              <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{g.files.length}</span>
            </div>
            {g.files.map((v) => (
          <div key={v.v} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 2, padding: '1px 5px' }}>v{v.v}</span>
            <span style={{ color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name="file-text" size={14} /></span>
            <span style={{ minWidth: 0, flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.file}</span>
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>{t(lang, KIND_LABEL[v.kind ?? 'main'].ru, KIND_LABEL[v.kind ?? 'main'].en)}</span>
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{v.size}</span>
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{v.date}</span>
            <button onClick={() => void downloadFile(doc.no, rev, v.v)} aria-label={t(lang, 'Скачать', 'Download')} className="icon-btn" style={{ display: 'grid', placeItems: 'center', width: 24, height: 24, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer' }}><Icon name="download" size={14} /></button>
          </div>
            ))}
            {/* Состояние текстового слоя показывается у основного файла версии:
                у скана поиск по содержимому не работает, и человек имеет право
                это видеть, а не гадать, почему документ не находится словами
                из него. */}
            {g.ver ? <TextState doc={doc.no} rev={rev!} ver={g.ver} lang={lang} /> : null}
          </div>
        ))
      ) : (
        <div style={note()}>{t(lang, `В ревизии ${rev} файлов нет.`, `Revision ${rev} has no files.`)}</div>
      )}

      {/* Строка состояния называет шаг словами, а спиннер показывает, что шаг
          ещё идёт: без него строка неотличима от вставшей загрузки. */}
      {step ? (
        <div style={{ ...note(), display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={13} />
          {`${t(lang, 'Загрузка', 'Upload')}: ${t(lang, STEP_LABEL[step].ru, STEP_LABEL[step].en)}…`}
        </div>
      ) : failed ? (
        <div style={note(true)}>{`${t(lang, 'Загрузка остановилась на шаге', 'Upload stopped at step')}: ${t(lang, STEP_LABEL[failed].ru, STEP_LABEL[failed].en)}`}</div>
      ) : null}

      {/* Кнопка нужна не только документу БЕЗ ревизии. Принятый в AFD документ
          выпускает следующую — и до сих пор это было нечем сделать: условие
          смотрело на наличие ревизии, а документ заводится сразу с «00», и
          кнопка не показывалась никому и никогда. Правило то же, что на
          сервере (RegisterCommands.REVISABLE): ревизия растёт только из
          приёмки. Код B закрывает круг ВЕРСИЕЙ — её кладут кнопкой загрузки
          файла ниже и подают трансмитталом. */}
      {rev == null || doc.life === 'afd' ? (
        <Button variant="secondary" size="sm" block iconLeft={<Icon name="file-plus" size={14} />} style={{ marginTop: 2 }}
                disabled={busy} onClick={() => issueRev.mutate()}>
          {issueRev.isPending ? t(lang, 'Выпускается…', 'Issuing…')
            : rev == null ? t(lang, 'Выпустить ревизию', 'Issue revision')
            : t(lang, `Выпустить следующую ревизию (после ${rev})`, `Issue the next revision (after ${rev})`)}
        </Button>
      ) : null}
      {rev != null ? (
        <>
          <input ref={fileRef} type="file" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = '' }} />
          <Select size="sm" value={kind} onChange={(e) => setKind(e.target.value)} disabled={busy} style={{ marginTop: 2 }}>
            {(Object.keys(KIND_LABEL) as (keyof typeof KIND_LABEL)[]).map((k) => (
              <option key={k} value={k}>{t(lang, KIND_LABEL[k].ru, KIND_LABEL[k].en)}</option>
            ))}
          </Select>
          {/* Пока идут три шага, кнопка выключена: второе нажатие выпустило бы
              второе намерение и вторую версию того же файла. */}
          {/* tdms-rail: главная нажимаемая зона панели отвечала только курсором —
              hover и active приходят готовыми правилами ui.css. */}
          <button disabled={busy} onClick={() => fileRef.current?.click()} className="tdms-rail"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px', marginTop: 2, border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--text-subtle)', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
            <Icon name="upload" size={15} />{t(lang, `Загрузить файл в ревизию ${rev}`, `Upload a file to revision ${rev}`)}
          </button>
          {expectedName ? (
            <div style={{ marginTop: 4, font: 'var(--font-caption)', color: 'var(--text-subtle)', wordBreak: 'break-all' }}>
              {kind === 'original'
                ? t(lang, `Имя файла: ${expectedName.replace(/\.[^.]+$/, '')}.<расширение исходника>`,
                          `File name: ${expectedName.replace(/\.[^.]+$/, '')}.<source extension>`)
                : t(lang, `Имя файла: ${expectedName}`, `File name: ${expectedName}`)}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

// ---- Sheet preview placeholder ----------------------------------------
function SheetPreview({ doc, onOpenViewer }: { doc: Doc | Vdoc; onOpenViewer: (no: string) => void }) {
  return (
    <button onClick={() => onOpenViewer(doc.no)} className="tdms-shprev" style={{ display: 'block', width: '100%', border: 0, padding: 0, cursor: 'pointer', background: 'transparent' }}>
      {/* Крупная нажимаемая зона отвечала только курсором. Рамка ведёт к клику
          акцентом; hover — строго под указателем, нажатие — и на касании.
          !important — рамка задана инлайном (приём .tdms-rail:hover). Scale не
          нужен: площадь велика, сдвиг читался бы как рывок всего листа. */}
      <style>{`
        @media (hover: hover) and (pointer: fine) { .tdms-shprev:hover > div { border-color: var(--accent) !important; } }
        .tdms-shprev:active > div { border-color: var(--accent) !important; }
      `}</style>
      <div style={{ position: 'relative', aspectRatio: '1.414 / 1', background: 'var(--surface-sunken)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', transition: 'border-color var(--duration-fast) var(--ease-standard)' }}>
        <div style={{ position: 'absolute', inset: 14, border: '1.5px solid var(--border-strong)' }} />
        <div style={{ position: 'absolute', right: 14, bottom: 14, width: 150, height: 64, border: '1.5px solid var(--border-strong)', background: 'var(--surface-card)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, borderBottom: '1px solid var(--border-default)', padding: '3px 5px', fontFamily: 'var(--font-mono)', fontSize: 7, color: 'var(--text-subtle)' }}>{doc.no}</div>
          <div style={{ height: 16, display: 'flex' }}><span style={{ flex: 1, borderRight: '1px solid var(--border-default)' }} /><span style={{ width: 34, display: 'grid', placeItems: 'center', fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--text-muted)' }}>{doc.rev}</span></div>
        </div>
        {[28, 46, 64].map((top, i) => <span key={i} style={{ position: 'absolute', left: 30, right: 180, top: top + '%', height: 1.5, background: 'var(--slate-300)' }} />)}
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', background: 'var(--slate-900)', color: 'var(--slate-100)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600 }}><span style={{ color: 'var(--amber-300)', display: 'inline-flex' }}><Icon name="pencil" size={14} /></span>Открыть просмотрщик с разметкой</span>
        </div>
      </div>
    </button>
  )
}

// ---- Card ---------------------------------------------------------------
/**
 * Аннулирование документа. Не удаление: строка остаётся в реестре со статусом
 * «Аннулирован», иначе из трансмитталов и замечаний исчезала бы ссылка, а из
 * отчётов — история. Подтверждение обязательно: действие видно всему проекту.
 */
function VoidDocDialog({ open, onClose, no, lang, onDone }: { open: boolean; onClose: () => void; no: string; lang: Lang; onDone: () => void }) {
  const [reason, setReason] = useState('')
  const voidDoc = useCommand<{ reason: string }>({
    path: () => `/docs/${encodeURIComponent(no)}/void`,
    body: (a) => ({ reason: a.reason }),
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.all('mdrs'), qk.all('vdrs'), qk.all('audit'),
      ...inboxKeys],
  })
  const submit = () => {
    voidDoc.mutate({ reason: reason.trim() }, {
      onSuccess: () => {
        toast({ tone: 'info', title: 'Документ аннулирован', text: `${no} выведен из обращения` })
        setReason('')
        onDone()
      },
    })
  }
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Аннулировать документ', 'Void document')}>
      <div style={{ width: 460, maxWidth: '100%' }}>
        <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang,
            `${no} останется в реестре со статусом «Аннулирован»: ссылки из трансмитталов и замечаний не должны обрываться. Из счётчиков и сроков он уйдёт.`,
            `${no} stays in the register marked as void: references from transmittals and comments must not break. It leaves the counters and the schedule.`)}
        </div>
        <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Причина', 'Reason')}</label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t(lang, 'например: заведён по ошибке', 'e.g. created by mistake')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="danger" iconLeft={<Icon name="ban" size={14} />} disabled={voidDoc.isPending} onClick={submit}>
            {voidDoc.isPending ? t(lang, 'Аннулирование…', 'Voiding…') : t(lang, 'Аннулировать', 'Void')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Приостановка работы по документу.
 *
 * <p>Состояний было восемь, и все они — движение вперёд плюс «аннулирован». А
 * работа встаёт постоянно: заказчик заморозил узел до решения по изменению,
 * подрядчик ждёт исходных данных. Отметить это было нечем — строка с первого
 * дня числилась просроченной, и убрать её из отчёта можно было только
 * аннулированием, то есть выбросив из состава реестра.
 *
 * <p>Причина обязательна: приостановка без причины неотличима от пропажи
 * документа, а объясняться по ней придётся на совещании через полгода.
 */
function HoldDocDialog({ open, onClose, no, lang }: { open: boolean; onClose: () => void; no: string; lang: Lang }) {
  const [reason, setReason] = useState('')
  const hold = useCommand<{ reason: string }>({
    path: () => `/docs/${encodeURIComponent(no)}/hold`,
    body: (a) => ({ reason: a.reason }),
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.item('docs', no), qk.item('vdocs', no),
      qk.all('mdrs'), qk.all('vdrs'), qk.all('audit')],
  })
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Приостановить документ', 'Put the document on hold')}>
      <div style={{ width: 460, maxWidth: '100%' }}>
        <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang,
            `${no} останется в составе реестра, но перестанет числиться просроченным: по нему не идёт работа. Возврат вернёт то состояние, из которого документ вывели.`,
            `${no} stays in the register but stops counting as overdue: no work is going on. Resuming brings back the state it was stopped in.`)}
        </div>
        <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Причина', 'Reason')}</label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder={t(lang, 'например: узел заморожен до решения по изменению', 'e.g. node frozen pending a change decision')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" iconLeft={<Icon name="pause" size={14} />} disabled={hold.isPending || !reason.trim()}
                  onClick={() => hold.mutate({ reason: reason.trim() }, {
                    onSuccess: () => { toast({ tone: 'info', title: t(lang, 'Документ приостановлен', 'Document on hold'), text: no }); setReason(''); onClose() },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не приостановлен', 'Not held'), text: e.message }),
                  })}>
            {hold.isPending ? t(lang, 'Приостановка…', 'Holding…') : t(lang, 'Приостановить', 'Hold')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Возврат приостановленного документа в работу.
 *
 * <p>Сдвиг прогноза на длительность простоя — отдельное решение, а не
 * побочный эффект: заморозка на сорок дней обычно двигает ожидание на сорок
 * дней, но бывает и наоборот — срок остался прежним, и навёрстывать придётся
 * подрядчику. План не двигается в любом случае: он обязательство.
 */
function ResumeDocDialog({ open, onClose, no, lang }: { open: boolean; onClose: () => void; no: string; lang: Lang }) {
  const [shift, setShift] = useState(true)
  const resume = useCommand<{ shift: boolean }, { life: string; days: number; shifted: boolean }>({
    path: () => `/docs/${encodeURIComponent(no)}/resume`,
    body: (a) => ({ shift: a.shift }),
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.item('docs', no), qk.item('vdocs', no),
      qk.all('mdrs'), qk.all('vdrs'), qk.all('audit')],
  })
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Вернуть документ в работу', 'Resume the document')}>
      <div style={{ width: 460, maxWidth: '100%' }}>
        <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, `${no} вернётся в то состояние, из которого его вывели: круг рассмотрения заново не разыгрывается.`,
                   `${no} returns to the state it was stopped in: the review round is not replayed.`)}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          <input type="checkbox" checked={shift} onChange={(e) => setShift(e.target.checked)} />
          {t(lang, 'Сдвинуть прогноз на длительность простоя', 'Move the forecast by the length of the hold')}
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" iconLeft={<Icon name="play" size={14} />} disabled={resume.isPending}
                  onClick={() => resume.mutate({ shift }, {
                    onSuccess: (r) => {
                      toast({ tone: 'success', title: t(lang, 'Документ в работе', 'Document resumed'),
                        text: t(lang, `простой ${r.days} дн.${r.shifted ? ', прогноз сдвинут' : ''}`,
                                      `held for ${r.days} days${r.shifted ? ', forecast moved' : ''}`) })
                      onClose()
                    },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не возвращён', 'Not resumed'), text: e.message }),
                  })}>
            {resume.isPending ? t(lang, 'Возврат…', 'Resuming…') : t(lang, 'Вернуть в работу', 'Resume')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Сроки по стадиям: три строки, у каждой план, прогноз и факт.
 *
 * <p>Каждая строка сохраняется своей командой — стадии независимы, и
 * сохранение всех трёх одной кнопкой означало бы, что правка срока выдачи
 * переписывает и срок приёмки, которого никто не трогал.
 */
function StageDates({ doc, lang, dateMin }: { doc: Doc | Vdoc; lang: Lang; dateMin?: Date }) {
  const path = '/docs/' + encodeURIComponent(doc.no) + '/schedule'
  const query = useSub<StageSchedule[]>(path)
  const [draft, setDraft] = useState<Record<string, StageSchedule>>({})
  const save = useCommand<{ stage: string; plan: string; forecast: string; fact: string; clearFact: boolean; clearPlan: boolean }>({
    method: 'PUT',
    path: (a) => `${path}/${a.stage}`,
    /* Пустой факт означает «не трогать»: отметку о сдаче ставит сам
       документооборот, и сохранение прогноза не должно её стирать. Снять её
       можно только осознанно — очистив поле у стадии, где она была.

       С планом то же: он договорный, и очистка поля — осознанное снятие
       обязательства, а не побочный итог сохранения прогноза. */
    body: (a) => ({ plan: a.plan, forecast: a.forecast, fact: a.fact, clearFact: a.clearFact, clearPlan: a.clearPlan }),
    invalidate: () => [qk.sub(path), qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })
  const rows = query.data ?? []
  const valueOf = (r: StageSchedule): StageSchedule => draft[r.stage] ?? r
  const changed = (r: StageSchedule) => {
    const v = valueOf(r)
    return (v.plan ?? '') !== (r.plan ?? '') || (v.forecast ?? '') !== (r.forecast ?? '') || (v.fact ?? '') !== (r.fact ?? '')
  }
  const set = (r: StageSchedule, key: keyof StageSchedule, value: string) =>
    setDraft((d) => ({ ...d, [r.stage]: { ...valueOf(r), [key]: value } }))
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <label style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
        {t(lang, 'Сроки по стадиям', 'Schedule by stage')}
      </label>
      {rows.map((r) => (
        /* Ряд переносится по строкам, а поля даты сжимаются: три поля даты с
           кнопкой требуют около 770 px, а окно правки — 520, и колонка «Факт»
           уходила за край окна вместе с кнопкой «Сохранить». */
        <div key={r.stage} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'end' }}>
          <span style={{ flex: '0 0 auto', width: 44, fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', paddingBottom: 8 }}>{r.stage}</span>
          <div style={{ flex: '1 1 130px', minWidth: 0 }}>
            <DateInput value={valueOf(r).plan ?? ''} onChange={(v) => set(r, 'plan', v)} minDate={dateMin} lang={lang} placeholder={t(lang, 'План', 'Plan')} />
          </div>
          <div style={{ flex: '1 1 130px', minWidth: 0 }}>
            <DateInput value={valueOf(r).forecast ?? ''} onChange={(v) => set(r, 'forecast', v)} minDate={dateMin} lang={lang} placeholder={t(lang, 'Прогноз', 'Forecast')} />
          </div>
          {/* Факт назад во времени законен: его ставят по случившемуся. */}
          <div style={{ flex: '1 1 130px', minWidth: 0 }}>
            <DateInput value={valueOf(r).fact ?? ''} onChange={(v) => set(r, 'fact', v)} lang={lang} placeholder={t(lang, 'Факт', 'Actual')} />
          </div>
          <Button variant="secondary" size="sm" disabled={!changed(r) || save.isPending}
                  onClick={() => save.mutate({ stage: r.stage, plan: valueOf(r).plan ?? '', forecast: valueOf(r).forecast ?? '',
                                               fact: valueOf(r).fact ?? '',
                                               clearFact: Boolean(r.fact) && !valueOf(r).fact,
                                               clearPlan: Boolean(r.plan) && !valueOf(r).plan }, {
                    onSuccess: () => toast({ tone: 'success', title: t(lang, 'Сроки сохранены', 'Schedule saved'), text: doc.no + ' · ' + r.stage }),
                  })}>{t(lang, 'Сохранить', 'Save')}</Button>
        </div>
      ))}
    </div>
  )
}

/**
 * Правка карточки. Меняются описательные поля и сроки по стадиям; номер,
 * ревизия и статус сюда не попадают — их выводит документооборот, и правка
 * руками рассинхронизировала бы карточку с трансмитталами и рассмотрением.
 */
/* Подпись поля диалога: остальные поля подписаны так же (eyebrow, капитель),
   и два вида подписей в одной форме читались бы как две разные формы. */
const CAPS = { font: 'var(--font-eyebrow)', textTransform: 'uppercase' as const, letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }

function EditDocDialog({ open, onClose, doc, lang }: { open: boolean; onClose: () => void; doc: Doc | Vdoc; lang: Lang }) {
  /* У документа поставщика (Vdoc) рецензента и критичности в типе нет — они
     есть у документа реестра заказчика. Читаем мягко: диалог один на оба, и
     ветвление по типу здесь означало бы две копии формы. */
  const extra = doc as { sheet?: string; reviewer?: string; crit?: boolean; area?: string }
  const [form, setForm] = useState({
    title: doc.title, titleEn: doc.titleEn ?? '', disc: doc.disc, type: doc.type,
    purpose: doc.purpose as string, sheet: extra.sheet ?? '', reviewer: extra.reviewer ?? '',
    /* Титул и объект правятся здесь же: строка ушла в другой титул того же
       объекта — событие обычное, а до сих пор титул вообще не хранился и
       выводился по объекту наугад. Номер от правки не меняется: выданный номер
       не пересобирают. */
    titul: doc.titul ?? '', area: extra.area ?? '',
  })
  const [crit, setCrit] = useState<boolean>(!!extra.crit)
  /* Справочники берутся те же, по которым сервер проверяет строку: свободный
     ввод давал «значение вне справочника» уже после нажатия. */
  const discRows = useDictRows('disc')
  const typeRows = useDictRows('docType')
  const purposeRows = useDictRows('purpose')
  const titulRows = useDictRows('titul')
  const areaRows = useDictRows('area')
  /* Историческую дату (план/прогноз в прошлом) вправе поставить только
     администратор — решение заказчика от 17.08.26. Остальным календарь
     закрывает прошедшие дни; проверка клиентская, сервер формат проверяет сам. */
  const isAdmin = useMe().data?.perms?.admin === 'rw'
  const dateMin = isAdmin ? undefined : today()
  /* Перенумерация и перенос в другой реестр — та же команда правки карточки,
     но своими полями: номер печатается в штампе и стоит в переписке, поэтому
     он отделён от обычных полей и предупреждён. */
  const [newNo, setNewNo] = useState('')
  const [newRegister, setNewRegister] = useState('')
  const move = useCommand<{ no?: string; register?: string }>({
    method: 'PATCH',
    path: () => `/docs/${encodeURIComponent(doc.no)}`,
    body: (a) => a,
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.all('mdrs'), qk.all('vdrs'), qk.all('audit')],
  })
  const edit = useCommand<typeof form>({
    method: 'PATCH',
    path: () => `/docs/${encodeURIComponent(doc.no)}`,
    /* Уходит только изменённое: непереданное поле сервер не трогает, и правка
       наименования не должна переписывать чужие сроки заодно. */
    body: (a) => ({
      ...Object.fromEntries(Object.entries(a).filter(([k, v]) => v !== (
        { title: doc.title, titleEn: doc.titleEn ?? '', disc: doc.disc, type: doc.type,
          purpose: doc.purpose as string, sheet: extra.sheet ?? '',
          reviewer: extra.reviewer ?? '', titul: doc.titul ?? '',
          area: extra.area ?? '' } as Record<string, string>)[k])),
      // Критичность булева: «не менялась» здесь — это совпадение со значением,
      // а не пустая строка, поэтому она сравнивается отдельно.
      ...(crit === !!extra.crit ? {} : { crit }),
    }),
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })
  const field = (key: keyof typeof form, label: string, hint?: string) => (
    <div>
      <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{label}</label>
      <Input value={form[key]} placeholder={hint} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
    </div>
  )
  /* Дисциплина, тип и назначение — справочники проекта, и сервер отбивает всё
     остальное («значение вне справочника»). Свободным вводом это узнавалось
     только отказом, а набор значений человек держал в голове. */
  const dictField = (key: 'disc' | 'type' | 'purpose', label: string, rows: { code: string; name: string }[]) => (
    <div>
      <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{label}</label>
      <Select size="sm" value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}>
        {/* Пустое значение остаётся выбираемым: у документа поле может быть не
            заполнено, и список не вправе назначать его молча. */}
        <option value="">{t(lang, '— не задано —', '— none —')}</option>
        {rows.map((r) => <option key={r.code} value={r.code}>{r.code}{r.name ? ' — ' + r.name : ''}</option>)}
      </Select>
    </div>
  )
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Изменить карточку', 'Edit card')}>
      <div style={{ width: 520, maxWidth: '100%', display: 'grid', gap: 12 }}>
        {field('title', t(lang, 'Наименование', 'Title'))}
        {/* Английское наименование печатается в бланке трансмиттала: без правки
            его можно было задать только при заведении документа. */}
        {field('titleEn', t(lang, 'Наименование (EN)', 'Title (EN)'), t(lang, 'Печатается в бланке', 'Printed on the blank'))}
        {/* minmax(0, 1fr), а не 1fr: у 1fr минимум по содержимому, а поле ввода
            меньше двухсот пикселей не считает себя, и три поля в ряд требовали
            620 px в окне шириной 520 — правый столбец уходил за край. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
          {dictField('disc', t(lang, 'Дисциплина', 'Discipline'), discRows)}
          {dictField('type', t(lang, 'Тип', 'Type'), typeRows)}
          {dictField('purpose', t(lang, 'Назначение', 'Purpose'), purposeRows)}
        </div>
        {/* Титул и объект: у титула объект один, поэтому выбор титула его же и
            задаёт. Справочники большие — выбор идёт поиском. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
          <DictPicker lang={lang} label={<span style={CAPS}>{t(lang, 'Титул проекта', 'Project title')}</span>} value={form.titul}
                      options={titulRows.map((r) => ({ code: r.code, name: r.name, note: r.area }))}
                      onPick={(code) => setForm((f) => ({
                        ...f, titul: code,
                        area: code ? (titulRows.find((r) => r.code === code)?.area ?? f.area) : f.area,
                      }))} />
          <DictPicker lang={lang} label={<span style={CAPS}>{t(lang, 'Объект / площадка', 'Area')}</span>} value={form.area}
                      options={areaRows.map((r) => ({ code: r.code, name: r.name }))}
                      onPick={(code) => setForm((f) => ({ ...f, area: code }))}
                      disabled={!!form.titul}
                      hint={form.titul ? t(lang, 'Задан титулом', 'Set by the title') : undefined} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: 12, alignItems: 'end' }}>
          {field('sheet', t(lang, 'Лист', 'Sheet'), '00')}
          {field('reviewer', t(lang, 'Рецензент', 'Reviewer'), t(lang, 'Кто смотрит документ', 'Who reviews it'))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', paddingBottom: 8 }}>
            <input type="checkbox" checked={crit} onChange={(e) => setCrit(e.target.checked)} />
            {t(lang, 'Критичный', 'Critical')}
          </label>
        </div>
        {/* Сроки ведут по стадиям: план, прогноз и факт у каждой свои. Общие
            даты документа больше не правятся здесь — они производная от
            действующей стадии, и сервер пересчитывает их сам. */}
        <StageDates doc={doc} lang={lang} dateMin={dateMin} />
        {/* Перенумерация и перенос в другой реестр: заказчик прислал уточнённую
            кодировку, строку завели не в тот реестр. Команда была на сервере с
            самого начала, а нажать её было негде — правили из базы.

            Отдельно от прочих полей и с предупреждением: номер печатается в
            штампе, стоит в переписке и в именах файлов. */}
        <details style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
          <summary style={{ cursor: 'pointer', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {t(lang, 'Перенумеровать или перенести в другой реестр', 'Renumber or move to another register')}
          </summary>
          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Номер печатается в штампе, стоит в переписке и в именах файлов. Сервер отобьёт номер, не подходящий маске реестра, и занятый номер.',
                       'The number is printed in the title block and used in correspondence and file names. The server refuses a number that breaks the register mask or is already taken.')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: 10, alignItems: 'end' }}>
              <Input label={t(lang, 'Новый номер', 'New number')} value={newNo} onChange={(e) => setNewNo(e.target.value)}
                     placeholder={doc.no} />
              <Input label={t(lang, 'Другой реестр', 'Another register')} value={newRegister} onChange={(e) => setNewRegister(e.target.value)}
                     placeholder={t(lang, 'код MDR или VDR', 'MDR or VDR code')} />
              <Button variant="secondary" disabled={move.isPending || (!newNo.trim() && !newRegister.trim())}
                      onClick={() => move.mutate({
                        no: newNo.trim() || undefined,
                        register: newRegister.trim() || undefined,
                      }, {
                        onSuccess: () => { toast({ tone: 'success', title: t(lang, 'Строка перенесена', 'Document moved'),
                                                   text: newNo.trim() || newRegister.trim() })
                                           setNewNo(''); setNewRegister(''); onClose() },
                        onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не перенесено', 'Not moved'), text: e.message }),
                      })}>{t(lang, 'Применить', 'Apply')}</Button>
            </div>
          </div>
        </details>
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Ревизия и статус правке не подлежат: их выводит документооборот.',
            'Revision and status are not editable: the workflow derives them.')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={edit.isPending} onClick={() => edit.mutate(form, {
            onSuccess: () => { toast({ tone: 'success', title: 'Карточка изменена', text: doc.no }); onClose() },
          })}>{edit.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}</Button>
        </div>
      </div>
    </Dialog>
  )
}

function DocumentCard({ no, lang = 'ru', onClose, onOpenViewer, onOpenTrm, onOpenCrs }: {
  no: string; lang?: Lang; onClose: () => void; onOpenViewer: (no: string) => void
  /** Открыть карточку трансмиттала: код пакета в карточке — ссылка, а не подпись. */
  onOpenTrm?: (id: string) => void
  /** Перейти к замечанию на его экране — тем же путём, что ссылка из уведомления. */
  onOpenCrs?: (code: string) => void
}) {
  /* Документ лежит ровно в одном из двух реестров, и в каком — на входе
     неизвестно. Реестр заказчика спрашивается первым, реестр поставщика —
     только если документа там нет: 404 больше не повторяется по общей политике
     повторов, поэтому лишний заход стоит одного запроса, а не секунд ожидания. */
  const docQuery = useItem<Doc>('docs', no)
  const vdocQuery = useItem<Vdoc>('vdocs', docQuery.isError ? no : null)
  /* Замечаний по одному документу — десятки: их состав ограничен самим
     документом, и одной страницы достаточно. */
  const crsQuery = useList<Crs>('crs', { page: 0, pageSize: 200, filters: { doc: [no] }, sort: [{ id: 'id', desc: false }] })
  const [view, setView] = useState<'summary' | 'timeline' | 'preview'>('summary')
  const [voiding, setVoiding] = useState(false)
  /* Приостановка и возврат — свои диалоги: у первой обязательна причина, у
     второго есть решение о сдвиге прогноза на длительность простоя. */
  const [holding, setHolding] = useState(false)
  const [resuming, setResuming] = useState(false)
  const [editing, setEditing] = useState(false)
  /* Кнопка прячется по тому же правилу, каким отбивает сервер: право на запись
     в реестре заказчика. Прятать по своему условию значит однажды показать
     кнопку, которая отвечает отказом. */
  const canEdit = useMe().data?.perms?.mdr === 'rw'
  /* Возврат аннулированной строки: команда была на сервере с самого начала, а
     нажать её было негде — реестр правили из базы. */
  const restore = useCommand<void>({
    path: () => `/docs/${encodeURIComponent(no)}/restore`,
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.item('docs', no), qk.item('vdocs', no), qk.all('audit')],
  })

  const doc = docQuery.data ?? vdocQuery.data
  if (!doc) {
    // 404 от одного из реестров — норма: документ нашёлся в другом. Ошибка —
    // только когда записи не отдал ни один и причина не в её отсутствии.
    const loading = docQuery.isPending || vdocQuery.isPending
    const failed = [docQuery.error, vdocQuery.error].some((e) => e != null && !(e instanceof ApiError && e.status === 404))
    return (
      <Drawer open onClose={onClose} title={null}>
        <div style={{ padding: 20, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: failed ? 'var(--danger-ink)' : 'var(--text-subtle)' }}>
          {loading ? t(lang, 'Загрузка…', 'Loading…')
            : failed ? t(lang, 'Не удалось загрузить документ', 'Failed to load the document')
              : t(lang, 'Документ не найден', 'Document not found')}
        </div>
      </Drawer>
    )
  }
  const crs = crsQuery.data?.rows ?? []
  const crsCount = crsQuery.data?.total ?? 0
  /* «Скачать» в шапке берёт последнюю версию действующей ревизии — ровно то,
     что показано в строке «Ревизия / версия». Без ревизии или без единой
     загруженной версии скачивать нечего, и кнопка гаснет. */
  const headRev = revOf(doc)
  const canDownload = headRev != null && doc.ver > 0

  const Header = (
    /* Своей кнопки «Закрыть» в шапке нет: она стоит в шапке самой выдвижной
       панели, и вторая давала два «Закрыть» в одном role=dialog. */
    <div style={{ flex: '0 0 auto', borderBottom: '1px solid var(--border-default)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px 12px' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <Crit on={'crit' in doc ? doc.crit : false} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 600, color: 'var(--text-strong)' }}>{doc.no}</span>
            <DiscTag code={doc.disc} />
          </div>
          <div style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{doc.title}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <Purpose value={doc.purpose} /><CodeChip code={doc.code} /><LifeBadge life={doc.life} />
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>· {doc.type} · {doc.vendor}</span>
          </div>
          <LifeTrack life={doc.life} rev={doc.rev} ver={doc.ver} lang={lang} />
        </div>
      </div>
      {/* Ряд переносится по строкам. Действий у карточки до семи — вместе с
          переключателем вида это около 1049 px, а панель шириной 720 и на
          ноутбуке шире не станет. Без переноса «Приостановить» и «Аннулировать»
          лежали за правым краем ЭКРАНА: прокрутки по горизонтали ни у одного
          предка нет, и нажать их было нельзя вовсе. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 20px 12px', flexWrap: 'wrap' }}>
        <Segmented size="sm" value={view} onChange={setView} options={[
          { value: 'summary', label: t(lang, 'Сводка', 'Summary'), icon: 'layout-list' },
          { value: 'timeline', label: t(lang, 'Хронология', 'Timeline'), icon: 'git-commit-vertical' },
          { value: 'preview', label: t(lang, 'Документ', 'Document'), icon: 'file-text' },
        ]} />
        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />}
                  disabled={!canDownload}
                  title={canDownload ? undefined : t(lang, 'Файлов у документа ещё нет', 'The document has no files yet')}
                  onClick={() => { if (headRev) void downloadFile(doc.no, headRev, doc.ver) }}>{t(lang, 'Скачать', 'Download')}</Button>
          {canEdit ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={14} />}
                    disabled={doc.life === 'void'}
                    title={doc.life === 'void' ? t(lang, 'Аннулированный документ не правится', 'A void document is not editable') : undefined}
                    onClick={() => setEditing(true)}>{t(lang, 'Изменить', 'Edit')}</Button>
          ) : null}
          <Button variant="primary" size="sm" iconLeft={<Icon name="pencil" size={14} />} onClick={() => onOpenViewer(doc.no)}>{t(lang, 'Просмотр и разметка', 'View & markup')}</Button>
          {/* Аннулирование стоит после основного действия и в опасном виде:
              обратного хода у него нет, промахнуться мимо «Просмотра» нельзя. */}
          {/* Возврат аннулированной строки в состав: заказчик исключил
              документ из объёма, а через месяц вернул. Команда была на сервере
              с самого начала, а нажать её было негде — реестр правили из базы. */}
          {canEdit && doc.life === 'void' ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="rotate-ccw" size={14} />}
                    disabled={restore.isPending}
                    title={t(lang, 'Вернуть документ в состав реестра', 'Bring the document back into the register')}
                    onClick={() => restore.mutate(undefined, {
                      onSuccess: () => toast({ tone: 'success', title: t(lang, 'Документ возвращён', 'Document restored'), text: doc.no }),
                      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не возвращён', 'Not restored'), text: e.message }),
                    })}>{t(lang, 'Вернуть в состав', 'Restore')}</Button>
          ) : null}
          {/* Приостановка стоит рядом с аннулированием, но она обратима:
              работа встала, а документ из состава реестра не выброшен. */}
          {canEdit && doc.life === 'hold' ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="play" size={14} />}
                    onClick={() => setResuming(true)}>{t(lang, 'Вернуть в работу', 'Resume')}</Button>
          ) : canEdit && doc.life !== 'void' && doc.life !== 'superseded' ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="pause" size={14} />}
                    title={t(lang, 'Работа встала: документ перестанет числиться просроченным',
                                   'Work has stopped: the document stops counting as overdue')}
                    onClick={() => setHolding(true)}>{t(lang, 'Приостановить', 'Hold')}</Button>
          ) : null}
          <Button variant="danger" size="sm" iconLeft={<Icon name="ban" size={14} />}
                  disabled={doc.life === 'void'}
                  title={doc.life === 'void' ? t(lang, 'Документ уже аннулирован', 'Already void') : undefined}
                  onClick={() => setVoiding(true)}>{t(lang, 'Аннулировать', 'Void')}</Button>
        </div>
      </div>
    </div>
  )

  return (
    <Drawer open onClose={onClose} title={Header}>
      <div style={{ flex: 1, overflow: 'auto', padding: 20, background: 'var(--surface-page)' }}>
        {view === 'summary' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Panel title={t(lang, 'Сведения', 'Details')}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <Field label={t(lang, 'Тип', 'Type')}>{doc.type}</Field>
                <Field label={t(lang, 'Ревизия / версия', 'Rev / ver')} mono>{doc.rev} · v{doc.ver}</Field>
                <Field label={t(lang, 'Источник (TRM)', 'Source TRM')} mono>{doc.trm ? <CodeLink code={doc.trm} onOpen={onOpenTrm} /> : '—'}</Field>
                <Field label={t(lang, 'Поставщик', 'Vendor')}>{doc.vendor}</Field>
                <Field label={t(lang, 'Рецензент', 'Reviewer')}>{('reviewer' in doc ? doc.reviewer : '') || '—'}</Field>
                <Field label={t(lang, 'Критичность', 'Critical')}>{('crit' in doc && doc.crit) ? <span style={{ color: 'var(--danger-ink)' }}>Да</span> : 'Нет'}</Field>
              </div>
            </Panel>
            {/* Колонки реестра заказчика, которых нет в схеме: тег, система,
                дата задания. Панель стояла на «Хронологии» — там, где смотрят
                историю передач, а не правят карточку, и её не находили.
                Место атрибутов — рядом со сведениями: это они и есть. */}
            <Panel title={t(lang, 'Атрибуты проекта', 'Project attributes')}><DocAttrsPanel doc={doc} lang={lang} /></Panel>
            <Panel title={t(lang, 'Контроль сроков', 'Schedule control')}><DateControl doc={doc} lang={lang} /></Panel>
            <Panel title={t(lang, 'Замечания (CRS / ACSR)', 'Comments')} count={crsCount}><CrsMini crs={crs} lang={lang} onOpenCrs={onOpenCrs} /></Panel>
            <Panel title={t(lang, 'Ревизии', 'Revisions')}><RevisionTimeline doc={doc} lang={lang} onOpenTrm={onOpenTrm} /></Panel>
          </div>
        ) : view === 'timeline' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Panel title={t(lang, 'История ревизий и версий', 'Revision & version history')}><RevisionTimeline doc={doc} lang={lang} expanded onOpenTrm={onOpenTrm} /></Panel>
            {/* Чем документ приходил и чем уходил: собиралось на сервере и не
                показывалось нигде — карточка знала лишь «источник (TRM)». */}
            <Panel title={t(lang, 'История передач', 'Transfer history')}><TransferHistory doc={doc} lang={lang} onOpenTrm={onOpenTrm} /></Panel>
            {/* История правок самой карточки: кто и что менял в строке
                реестра. Прежде это читалось только журналом аудита по сессии. */}
            <Panel title={t(lang, 'История правок карточки', 'Card edit history')}><FieldHistory doc={doc} lang={lang} /></Panel>
            <Panel title={t(lang, 'Связи документа', 'Document links')}><DocLinks doc={doc} lang={lang} /></Panel>
            <Panel title={t(lang, 'Замечания по ревизиям', 'Comments')} count={crsCount}><CrsMini crs={crs} lang={lang} onOpenCrs={onOpenCrs} /></Panel>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20, alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <SheetPreview doc={doc} onOpenViewer={onOpenViewer} />
              <Panel title={t(lang, 'Файлы', 'Files')}><FilesList doc={doc} lang={lang} /></Panel>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <Panel title={t(lang, 'Контроль сроков', 'Schedule')}><DateControl doc={doc} lang={lang} /></Panel>
              <Panel title={t(lang, 'Замечания', 'Comments')} count={crsCount}><CrsMini crs={crs} lang={lang} onOpenCrs={onOpenCrs} /></Panel>
            </div>
          </div>
        )}
      </div>
      <VoidDocDialog open={voiding} onClose={() => setVoiding(false)} no={doc.no} lang={lang}
                     onDone={() => { setVoiding(false); onClose() }} />
      {holding ? <HoldDocDialog open onClose={() => setHolding(false)} no={doc.no} lang={lang} /> : null}
      {resuming ? <ResumeDocDialog open onClose={() => setResuming(false)} no={doc.no} lang={lang} /> : null}
      {editing ? <EditDocDialog open onClose={() => setEditing(false)} doc={doc} lang={lang} /> : null}
    </Drawer>
  )
}

function Panel({ title, count, children }: { title: ReactNode; count?: number; children: ReactNode }) {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <h3 style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-muted)' }}>{title}</h3>
        {count != null ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 2, padding: '0 5px' }}>{count}</span> : null}
        <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
      </div>
      {children}
    </section>
  )
}

export { DocumentCard }

